import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import operatorApiService from "../../../services/operator-api.service";
import "./DownloadBpsLogModal.scss";

type Props = {
  isOpen: boolean;
  initialVehicleId: number | null;
  initialStart: string;
  initialEnd: string;
  delayOffsetMs: number;
  vehicleOptions: Array<{ value: number; label: string }>;
  onClose: () => void;
};

const parseDateTimeLocal = (value: string): Date | null => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date;
};

const formatLocalDateTimeForRequest = (date: Date) => {
  const pad2 = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(
    date.getDate(),
  )} ${pad2(date.getHours())}:${pad2(date.getMinutes())}:${pad2(
    date.getSeconds(),
  )}`;
};

const DownloadBpsLogModal = ({
  isOpen,
  initialVehicleId,
  initialStart,
  initialEnd,
  delayOffsetMs,
  vehicleOptions,
  onClose,
}: Props) => {
  const [vehicleId, setVehicleId] = useState<number | null>(initialVehicleId);
  const [startInput, setStartInput] = useState(initialStart);
  const [endInput, setEndInput] = useState(initialEnd);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const next =
      initialVehicleId !== null &&
      vehicleOptions.some((o) => o.value === initialVehicleId)
        ? initialVehicleId
        : (vehicleOptions[0]?.value ?? null);
    setVehicleId(next);
    setStartInput(initialStart);
    setEndInput(initialEnd);
  }, [isOpen, initialVehicleId, initialStart, initialEnd, vehicleOptions]);

  useEffect(() => {
    if (!isOpen) return;
    if (!vehicleOptions.length) return;
    setVehicleId((prev) =>
      vehicleOptions.some((o) => o.value === prev)
        ? prev
        : vehicleOptions[0].value,
    );
  }, [isOpen, vehicleOptions]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  const canDownload = useMemo(() => {
    const s = parseDateTimeLocal(startInput);
    const e = parseDateTimeLocal(endInput);
    return Boolean(vehicleId !== null && s && e && !isDownloading);
  }, [vehicleId, startInput, endInput, isDownloading]);

  const handleDownload = async () => {
    if (vehicleId === null) return;
    const startAt = parseDateTimeLocal(startInput);
    const endAt = parseDateTimeLocal(endInput);
    if (!startAt || !endAt) return;

    const safeEnd = endAt.getTime() < startAt.getTime() ? startAt : endAt;
    const startIso = formatLocalDateTimeForRequest(startAt);
    const endIso = formatLocalDateTimeForRequest(safeEnd);

    setIsDownloading(true);
    try {
      const url = operatorApiService.getDownloadBpsLogCsvUrl({
        vehicleId,
        start: startIso,
        end: endIso,
        delayOffsetMs,
      });

      window.location.assign(url);
      onClose();
    } catch (err) {
      console.error("bpslog download error:", err);
    } finally {
      setIsDownloading(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className="bpslogModalOverlay"
      role="dialog"
      aria-modal="true"
      aria-label="Log Download Modal"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bpslogModal">
        <div className="bpslogModalHeader">Log Download</div>

        <div className="bpslogModalBody">
          <label className="bpslogModalField">
            <span>車両</span>
            <select
              value={vehicleId ?? ""}
              onChange={(e) => setVehicleId(Number(e.target.value))}
              disabled={!vehicleOptions.length}
            >
              {!vehicleOptions.length ? (
                <option value="" disabled>
                  車両なし
                </option>
              ) : null}
              {vehicleOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>

          <label className="bpslogModalField">
            <span>開始</span>
            <input
              type="datetime-local"
              value={startInput}
              onChange={(e) => setStartInput(e.target.value)}
              step={1}
            />
          </label>

          <label className="bpslogModalField">
            <span>終了</span>
            <input
              type="datetime-local"
              value={endInput}
              onChange={(e) => setEndInput(e.target.value)}
              step={1}
            />
          </label>
        </div>

        <div className="bpslogModalFooter">
          <button
            type="button"
            className="bpslogButton"
            onClick={onClose}
            disabled={isDownloading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="bpslogButton bpslogButton--primary"
            onClick={handleDownload}
            disabled={!canDownload}
          >
            Download
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default DownloadBpsLogModal;
